import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { PaperCard } from "@/components/PaperCard";
import { JournalCard } from "@/components/JournalCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useOutletContext();
  const hideIF = settings?.show_impact_factor === false;
  const q = params.get("q") || "";
  const [query, setQuery] = useState(q);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!q) { setLoading(false); return; }
    setLoading(true);
    setQuery(q);
    api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => setRes(data)).finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const total = (res?.papers?.length || 0) + (res?.journals?.length || 0);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <form onSubmit={submit} className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input data-testid="search-page-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="pl-12 h-12 rounded-full" />
        </div>
        <Button type="submit" data-testid="search-page-btn" className="h-12 px-6 rounded-full">Search</Button>
      </form>

      {loading ? (
        <div className="min-h-[30vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : !q ? (
        <p className="text-slate-500">Enter a term to search papers and journals.</p>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-6">{total} result{total !== 1 ? "s" : ""} for <span className="font-semibold text-slate-900 dark:text-slate-200">"{q}"</span></p>

          {res?.journals?.length > 0 && (
            <section className="mb-10">
              <h2 className="font-serif font-bold text-xl mb-4">Journals</h2>
              <div className="grid md:grid-cols-2 gap-4">{res.journals.map((j) => <JournalCard key={j.slug} journal={j} hideImpactFactor={hideIF} />)}</div>
            </section>
          )}

          {res?.papers?.length > 0 && (
            <section>
              <h2 className="font-serif font-bold text-xl mb-4">Articles</h2>
              <div className="grid gap-4">{res.papers.map((p) => <PaperCard key={p.id} paper={p} />)}</div>
            </section>
          )}

          {total === 0 && <p className="text-slate-500">No matching results found.</p>}
        </>
      )}
    </div>
  );
}
